import { useEffect, useId, useState } from 'react';
import {
  CATALOG_VIEWS,
  CATALOG_VIEW_HINTS,
  setCatalogView,
  storedCatalogView,
  type CatalogView,
} from './catalog-view.js';
import {
  THEMES,
  resolveTheme,
  setTheme,
  storedTheme,
  watchSystemTheme,
  type ThemePreference,
} from './theme.js';

/**
 * How the app looks on this device: the theme (NFR-UX-5) and the catalog's
 * opening layout (FR-3.1a).
 *
 * Both belong here rather than in Account, which holds the identity and the
 * preferences that had nowhere else to go. These two are the same kind of
 * thing -- a choice about what this screen looks like, kept on this device, no
 * Save -- and each says where it is kept in its own words rather than printing
 * one sentence twice.
 *
 * Radio buttons rather than the `select` the reminder cadence and the currency
 * use. A theme is judged by looking at it: the
 * choices should be visible at once and one tap apart, not behind a popup that
 * covers the page being judged. It is also the shape that admits a third state --
 * "match my system" is a choice, not an empty slot (see `theme.ts`).
 *
 * The change takes effect on the press, and there is no Save. Nothing here can be
 * lost or half-applied, and a confirmation step would only put a button between
 * someone and the thing they are already looking at.
 */
export function Appearance() {
  const ids = { theme: useId(), catalog: useId() };
  const [preference, setPreference] = useState<ThemePreference>(storedTheme);
  const [catalogView, setView] = useState<CatalogView>(storedCatalogView);
  /** What `system` resolves to, re-read when the system flips. */
  const [system, setSystem] = useState(() => resolveTheme('system'));

  useEffect(() => watchSystemTheme(() => setSystem(resolveTheme('system'))), []);

  const choose = (next: ThemePreference) => {
    setPreference(next);
    setTheme(next);
  };

  return (
    <div>
      <h2>Appearance</h2>

      <div className="field">
        <span className="field-label" id={ids.theme}>
          Color theme
        </span>

        <div className="choices" role="radiogroup" aria-labelledby={ids.theme}>
          {THEMES.map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name="theme"
                value={option.value}
                checked={preference === option.value}
                onChange={() => choose(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>

        <p className="muted hint">
          {preference === 'system'
            ? // Named, because "match my system" is otherwise the one option that
              // does not say what it did -- and on a device that switches at
              // sunset, what it did changes without anyone touching this screen.
              `Following this device, which is currently ${system}.`
            : 'Kept on this device, so a phone and a laptop can differ.'}
        </p>
      </div>

      {/*
        FR-3.1a: which of the catalog's two renderings it opens on.

        Radio buttons rather than a `select`, to match the theme above it: two
        choices on a screen that already offers three this way would be an odd
        one out, and the hint under each option is what distinguishes them.

        Worded as what the catalog *opens* on, because that is the whole of what
        this does. The toolbar there still switches as freely as it did, and
        says nothing back to this setting -- a glance at the grid should not
        quietly rewrite what tomorrow starts on.
      */}
      <div className="field">
        <span className="field-label" id={ids.catalog}>
          Catalog opens as
        </span>

        <div className="choices" role="radiogroup" aria-labelledby={ids.catalog}>
          {CATALOG_VIEWS.map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name="catalog-view"
                value={option.value}
                checked={catalogView === option.value}
                onChange={() => {
                  setView(option.value);
                  setCatalogView(option.value);
                }}
              />
              {option.label}
            </label>
          ))}
        </div>

        <p className="muted hint">
          {CATALOG_VIEW_HINTS[catalogView]} Chosen per device, so a phone can open on photographs
          and a laptop on rows.
        </p>
      </div>
    </div>
  );
}
